import { useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2 } from "lucide-react";
import { useReportHistory } from "@/hooks/use-api";
import { apiGet } from "@/lib/api";
import type { ComplianceReportResponse } from "@/types/api";

const TYPE_LABELS: Record<string, string> = {
  soc2: "SOC 2",
  posture: "Posture",
  msp_portfolio: "MSP Portfolio",
};

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  completed: "default",
  pending: "secondary",
  generating: "secondary",
  failed: "destructive",
};

function formatDate(iso: string | null) {
  if (!iso) return "--";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSize(bytes: number | null) {
  if (!bytes) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ReportHistoryTableProps {
  typeFilter?: string;
}

export function ReportHistoryTable({ typeFilter }: ReportHistoryTableProps) {
  const [activeType, setActiveType] = useState(typeFilter || "");
  const { data, isLoading } = useReportHistory(activeType || undefined);
  const reports = data?.reports ?? [];

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = useCallback(async (reportId: string) => {
    setDownloadingId(reportId);
    try {
      const resp = await apiGet<ComplianceReportResponse>(
        `/api/v1/reports/compliance/${reportId}?download=true`,
      );
      if (resp.download_url) {
        window.open(resp.download_url, "_blank");
      }
    } catch {
      // silently fail
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[
          { value: "", label: "All" },
          { value: "soc2", label: "SOC 2" },
          { value: "posture", label: "Posture" },
          { value: "msp_portfolio", label: "MSP Portfolio" },
        ].map((opt) => (
          <Button
            key={opt.value}
            variant={activeType === opt.value ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveType(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : reports.length === 0 ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          No reports found.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Size</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <Badge variant="outline">
                    {TYPE_LABELS[r.report_type] ?? r.report_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[r.status] ?? "outline"}>
                    {r.status === "generating" && (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    )}
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {r.period_start} - {r.period_end}
                </TableCell>
                <TableCell>{formatDate(r.created_at)}</TableCell>
                <TableCell>{formatSize(r.file_size_bytes)}</TableCell>
                <TableCell className="text-right">
                  {r.status === "completed" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Download"
                      onClick={() => handleDownload(r.id)}
                      disabled={downloadingId === r.id}
                    >
                      {downloadingId === r.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
